import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Heading } from "../components/heading";
import { Text } from "../components/text";
import { MonthlyProduction } from "./reportes/MonthlyProduction";
import { useTarifas, useContracts } from "../contexts/DataContext";
import { ComisionPorAfianzadora } from "./reportes/ComisionPorAfianzadora";
import { ProduccionPorResponsable } from "./reportes/ProduccionPorResponsable";
import { ProduccionPorCliente } from "./reportes/ProduccionPorCliente";
import { ProduccionPorAfianzadora } from "./reportes/ProduccionPorAfianzadora";
import { TarifasPorCliente } from "./reportes/TarifasPorCliente";
import { LiquidacionComisiones } from "./reportes/LiquidacionComisiones";
import { CobranzaPrimas } from "./reportes/CobranzaPrimas";
import { SaldosCreditosClientes } from "./reportes/SaldosCreditosClientes";
import { MetasPorAfianzadora } from "./reportes/MetasPorAfianzadora";
import { CicloVidaFianzas } from "./reportes/CicloVidaFianzas";
import { Beneficiarios } from "./reportes/Beneficiarios";
import { EstadisticasCliente } from "./reportes/EstadisticasCliente";
import { useUser } from "../components/UserContext";
import { ShieldExclamationIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { getFechaEmision } from "../utils/calculations";
import { ExportPDFDialog } from "../components/ExportPDFDialog";
import { createPDFDocument, formatPeriod } from "../utils/pdfExport";
import "../styles/pdf-export.css";

// `operator: true` marks the reportes an operator may open: the ones with no
// comisión, no tarifa and no teammate comparison — cobranza, saldos y cartera,
// which is the work an operator actually does. Everything else stays with the
// owner. Note this is a relevance gate, not a security boundary: DataContext
// already loads every org client, contract and comisión movement for operators
// (the Firestore rules allow it), so hiding a report hides the presentation,
// not the data.
const REPORTS = [
  {
    id: "monthly-production",
    title: "Producción mensual",
    description: "Fianzas por mes con comisiones estimadas y resumen anual.",
  },
  {
    id: "comision-afianzadora",
    title: "Comisión por afianzadora",
    description: "Resumen de comisiones estimadas por afianzadora en un mes o año.",
  },
  {
    id: "liquidacion-comisiones",
    title: "Liquidación de comisiones",
    description: "Comisión cobrada vs pendiente y cargos (ledger), netos por afianzadora o cliente.",
  },
  {
    id: "cobranza-primas",
    operator: true,
    title: "Cobranza de primas",
    description: "Facturas sin pagar por antigüedad, con riesgo de buró, por cliente o afianzadora.",
  },
  {
    id: "saldos-clientes",
    operator: true,
    title: "Saldos de clientes",
    description: "Saldo por pagar y a favor por cliente y afianzadora, con créditos vivos.",
  },
  {
    id: "metas-afianzadora",
    title: "Metas por afianzadora",
    description: "Avance mensual de prima contra la meta anual de cada afianzadora.",
  },
  {
    id: "ciclo-vida",
    operator: true,
    title: "Ciclo de vida de fianzas",
    description: "Emisiones, renovaciones, sustituciones, cancelaciones y anulaciones por mes.",
  },
  {
    id: "beneficiarios",
    operator: true,
    title: "Beneficiarios",
    description: "A quién garantizan las fianzas del año: montos, clientes y afianzadoras por beneficiario.",
  },
  {
    id: "estadisticas-cliente",
    operator: true,
    title: "Estadísticas de la cartera",
    description: "Distribución, historial por año y vencimientos de toda la cartera, o de un cliente.",
  },
  {
    id: "produccion-responsable",
    title: "Producción por responsable",
    description: "Producción y comisiones por miembro del equipo. Incluye clientes sin asignar.",
  },
  {
    id: "produccion-cliente",
    title: "Producción por cliente",
    description: "Producción y comisiones por cliente. Visualiza qué clientes generan más.",
  },
  {
    id: "produccion-afianzadora",
    title: "Producción por afianzadora",
    description: "Producción y comisiones por afianzadora. Visualiza qué emisores generan más.",
  },
  {
    id: "tarifas-cliente",
    title: "Tarifas por cliente",
    description: "Análisis de tarifas promedio por cliente y sus fianzas.",
  },
];

const ReportCard = ({ report, selected, onSelect }) => {
  return (
    <button
      onClick={() => onSelect(report.id)}
      className={`w-full text-left bg-white border rounded-xl p-4 shadow-sm transition-all ${
        selected ? "border-azul-surelia ring-2 ring-azul-surelia/30" : "border-azul-surelia/15 hover:border-azul-surelia/40"
      }`}
    >
      <Heading level={3} className="text-lg text-azul-surelia mb-1">
        {report.title}
      </Heading>
      <Text className="text-sm text-azul-surelia/70">{report.description}</Text>
    </button>
  );
};

const Reportes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOwner, isOperator, loading } = useUser();

  // Access control: owners see everything; operators see only the reportes
  // flagged `operator`; everyone else is turned away.
  const isFullAccess = isOwner;
  const hasAccess = isFullAccess || isOperator;
  const visibleReports = useMemo(
    () => (isFullAccess ? REPORTS : REPORTS.filter((r) => r.operator)),
    [isFullAccess]
  );
  const canSeeReport = (id) => visibleReports.some((r) => r.id === id);

  const [selectedReportId, setSelectedReportId] = useState(() => {
    const requested = new URLSearchParams(location.search).get('reporte');
    return REPORTS.some((r) => r.id === requested) ? requested : REPORTS[0].id;
  });

  // Two-way ?reporte= sync: the URL always names the visible report, and an
  // external URL change (deep-link) switches the report.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reporte') === selectedReportId) return;
    params.set('reporte', selectedReportId);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReportId]);

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('reporte');
    if (requested && requested !== selectedReportId && canSeeReport(requested)) {
      setSelectedReportId(requested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const { tarifas, tarifasLoading } = useTarifas();
  const { contractsList } = useContracts();
  const now = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportingReportId, setExportingReportId] = useState(null);
  const reportContainerRef = useRef(null);
  const exportContainerRef = useRef(null);

  // The role resolves after the first render, and a deep-link can name a report
  // this user may not open — in both cases fall back to their first report.
  useEffect(() => {
    if (loading || !hasAccess) return;
    if (!canSeeReport(selectedReportId) && visibleReports.length > 0) {
      setSelectedReportId(visibleReports[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasAccess, visibleReports, selectedReportId]);

  // Compute available years from all fianzas
  const yearsAvailable = useMemo(() => {
    const set = new Set();
    contractsList.forEach((contract) => {
      (contract.fianzas || []).forEach((fianza) => {
        const fechaMs = getFechaEmision(fianza);
        if (fechaMs) {
          set.add(new Date(fechaMs).getFullYear());
        }
      });
    });
    if (set.size === 0) set.add(now.getFullYear());
    return Array.from(set).sort((a, b) => a - b);
  }, [contractsList, now]);

  // Auto-select valid year when data loads
  useEffect(() => {
    if (yearsAvailable.length > 0 && !yearsAvailable.includes(selectedYear)) {
      setSelectedYear(yearsAvailable[yearsAvailable.length - 1]);
    }
  }, [yearsAvailable, selectedYear]);

  useEffect(() => {
    // Trigger re-render when tarifas finish loading; no-op here, just dependency anchor
  }, [tarifas, tarifasLoading]);

  // Render a report component by ID
  const renderReportById = (reportId) => {
    if (!canSeeReport(reportId)) return null;
    switch (reportId) {
      case "monthly-production":
        return <MonthlyProduction selectedYear={selectedYear} />;
      case "comision-afianzadora":
        return <ComisionPorAfianzadora selectedYear={selectedYear} />;
      case "liquidacion-comisiones":
        return <LiquidacionComisiones selectedYear={selectedYear} />;
      case "cobranza-primas":
        return <CobranzaPrimas />;
      case "saldos-clientes":
        return <SaldosCreditosClientes />;
      case "metas-afianzadora":
        return <MetasPorAfianzadora selectedYear={selectedYear} />;
      case "ciclo-vida":
        return <CicloVidaFianzas selectedYear={selectedYear} />;
      case "beneficiarios":
        return <Beneficiarios selectedYear={selectedYear} />;
      case "estadisticas-cliente":
        return <EstadisticasCliente selectedYear={selectedYear} />;
      case "produccion-responsable":
        return <ProduccionPorResponsable selectedYear={selectedYear} />;
      case "produccion-cliente":
        return <ProduccionPorCliente selectedYear={selectedYear} />;
      case "produccion-afianzadora":
        return <ProduccionPorAfianzadora selectedYear={selectedYear} />;
      case "tarifas-cliente":
        return <TarifasPorCliente selectedYear={selectedYear} />;
      default:
        return null;
    }
  };

  // Export multiple reports to a single PDF
  const handleExportReports = useCallback(async (reports, onProgress) => {
    const period = formatPeriod(selectedYear);

    // Create a new PDF document
    const pdfDoc = await createPDFDocument();

    for (let i = 0; i < reports.length; i++) {
      const report = reports[i];

      // Update progress
      onProgress(i + 1, reports.length, report.title);

      // Set the report to render in export container
      setExportingReportId(report.id);

      // Wait for the report to render fully (charts, data, layout)
      await new Promise(resolve => setTimeout(resolve, 1200));

      // Add the report to the PDF
      if (exportContainerRef.current) {
        await pdfDoc.addReport(exportContainerRef.current, {
          title: report.title,
          period
        });
      }
    }

    // Clear the export container
    setExportingReportId(null);

    // Generate filename
    const filename = reports.length === 1
      ? `${reports[0].title.replace(/\s+/g, '_')}_${selectedYear}.pdf`
      : `Reportes_${selectedYear}.pdf`;

    // Save the combined PDF
    pdfDoc.save(filename);
  }, [selectedYear]);

  // Show loading while checking access
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  // Access denied for non-owners
  if (!hasAccess) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldExclamationIcon className="w-8 h-8 text-red-600" />
          </div>
          <Heading level={2} className="text-gray-900 mb-2">
            Acceso Restringido
          </Heading>
          <Text className="text-gray-600 mb-6">
            Los reportes solo están disponibles para propietarios de la organización.
          </Text>
          <button
            onClick={() => navigate("/app")}
            className="px-4 py-2 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <Heading level={1} className="text-3xl font-bold text-azul-surelia">
            Reportes
          </Heading>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="text-lg font-semibold border border-azul-surelia/20 rounded-lg px-3 py-1 bg-white text-azul-surelia focus:ring-2 focus:ring-azul-surelia/30 focus:border-azul-surelia"
            data-pdf-hide
          >
            {yearsAvailable.map((yr) => (
              <option key={yr} value={yr}>
                {yr}
              </option>
            ))}
          </select>
          {/* Every report in the export dialog is comisión o tarifa, so the
              button follows the same gate. */}
          {isFullAccess && (
            <button
              onClick={() => setIsExportDialogOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-azul-surelia/20 rounded-lg text-azul-surelia text-sm font-medium hover:bg-azul-surelia/5 hover:border-azul-surelia/40 transition-all duration-200"
              title="Exportar reportes a PDF"
            >
              <DocumentArrowDownIcon className="h-5 w-5" />
              <span>Exportar PDF</span>
            </button>
          )}
        </div>
        <Text className="text-azul-surelia/70">
          Selecciona un reporte para ver el detalle.
        </Text>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-pdf-hide>
        {visibleReports.map((report) => (
          <ReportCard
            key={report.id}
            report={report}
            selected={report.id === selectedReportId}
            onSelect={setSelectedReportId}
          />
        ))}
      </div>

      <div ref={reportContainerRef} className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-4">
        {renderReportById(selectedReportId)}
      </div>

      {/* Hidden export container - renders reports off-screen for PDF capture */}
      {exportingReportId && (
        <div
          style={{
            position: 'fixed',
            left: '-9999px',
            top: 0,
            width: '1100px',
            backgroundColor: '#ffffff',
          }}
        >
          <div
            ref={exportContainerRef}
            className="bg-white p-6"
            style={{ minHeight: '500px' }}
          >
            {renderReportById(exportingReportId)}
          </div>
        </div>
      )}

      {/* Export Dialog */}
      {isFullAccess && (
        <ExportPDFDialog
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
          year={selectedYear}
          currentReportId={selectedReportId}
          onExportReports={handleExportReports}
        />
      )}
    </div>
  );
};

export default Reportes;
